import type { Example } from '@cogo/kg-repository'
import { ONTOLOGY_CANDIDATE_QUALITY_FLOOR, W_TAGS, W_DS } from './types'

export { ONTOLOGY_CANDIDATE_QUALITY_FLOOR }

export interface PickInput {
  semanticType?: string
  tags?: string[]
  dsId?: string
}

function jaccard(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  if (a.size === 0 && b.size === 0) return 0
  let inter = 0
  for (const x of a) if (b.has(x)) inter++
  const union = a.size + b.size - inter
  return union === 0 ? 0 : inter / union
}

function readTags(ex: Example): string[] {
  // Spec stores tags on cogo:exampleTags; kg-repository projects it onto
  // Example via an extension property surfaced as `exampleTags` (string[]).
  // The base Example type omits it for forward-compat; we read defensively.
  const t = (ex as unknown as { exampleTags?: unknown }).exampleTags
  return Array.isArray(t) ? (t as string[]).filter((x) => typeof x === 'string') : []
}

function readDsCompat(ex: Example): Set<string> {
  const d = (ex as unknown as { dsCompatibility?: unknown }).dsCompatibility
  if (Array.isArray(d)) return new Set(d.filter((x) => typeof x === 'string') as string[])
  // Single dsId fallback — Example.dsId means "authored against".
  return new Set(ex.dsId ? [ex.dsId] : [])
}

function readScore(ex: Example): number {
  const s = (ex as unknown as { score?: unknown }).score
  return typeof s === 'number' && Number.isFinite(s) ? s : 0
}

export function readQualityGrade(ex: Example): number | null {
  const grade = (ex as unknown as { qualityGrade?: unknown }).qualityGrade
  return typeof grade === 'number' && Number.isFinite(grade) ? grade : null
}

export function ontologyCandidateQualityIssue(
  ex: Example,
  floor = ONTOLOGY_CANDIDATE_QUALITY_FLOOR,
): string | null {
  const grade = readQualityGrade(ex)
  if (grade == null) {
    return `missing rendered design-quality grade; floor=${floor}`
  }
  if (grade < floor) {
    return `rendered design-quality grade below floor (${grade} < ${floor})`
  }
  return null
}

export function isOntologyCandidateDesignReady(
  ex: Example,
  floor = ONTOLOGY_CANDIDATE_QUALITY_FLOOR,
): boolean {
  return ontologyCandidateQualityIssue(ex, floor) === null
}

export function pickTopOne(
  candidates: readonly Example[],
  input: PickInput,
): Example | null {
  if (candidates.length === 0) return null
  const designReadyCandidates = candidates.filter((ex) => isOntologyCandidateDesignReady(ex))
  if (designReadyCandidates.length === 0) return null

  const inputTags = new Set<string>([
    ...(input.semanticType ? [input.semanticType] : []),
    ...(input.tags ?? []),
  ])
  const inputDs = input.dsId

  const scored = designReadyCandidates.map((ex) => {
    const tagsOverlap = jaccard(inputTags, new Set(readTags(ex)))
    const dsBoost = inputDs && readDsCompat(ex).has(inputDs) ? 1 : 0
    const final = readScore(ex) + W_TAGS * tagsOverlap + W_DS * dsBoost
    return { ex, final }
  })

  // Sort desc by final, then asc by id (lex tiebreaker — deterministic).
  scored.sort((a, b) => {
    if (a.final !== b.final) return b.final - a.final
    return a.ex.id < b.ex.id ? -1 : a.ex.id > b.ex.id ? 1 : 0
  })

  return scored[0]!.ex
}
